/**
 * What a rail row is called and drawn with, in one place, so a row in a
 * mailbox's tree and its pin under Favorites can never be named differently.
 */
import { Archive, CalendarClock, FileText, Folder, Inbox, Send, ShieldAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { MailboxView } from "../../../../lib/mail";
import { ALL, type BoxRow, type MailView } from "./view";

type T = (key: string, vars?: Record<string, string | number>) => string;

export const ROW_ICON: Record<BoxRow, LucideIcon> = {
  inbox: Inbox,
  drafts: FileText,
  scheduled: CalendarClock,
  sent: Send,
  archived: Archive,
  spam: ShieldAlert,
};

const ROW_KEY: Record<BoxRow, string> = {
  inbox: "mail.folder.inbox",
  drafts: "drafts.draftsTitle",
  scheduled: "drafts.scheduledTitle",
  sent: "mail.box.sent",
  archived: "mail.folder.archived",
  spam: "mail.folder.spam",
};

export function rowLabel(t: T, row: BoxRow): string {
  return t(ROW_KEY[row]);
}

/** The row a view is, for the views that are one of a mailbox's six. */
export function rowOf(view: MailView): BoxRow | null {
  switch (view.kind) {
    case "threads":
      return view.status === "open" ? "inbox" : view.status;
    case "drafts":
      return view.scheduled ? "scheduled" : "drafts";
    case "sent":
    case "sentAll":
      return "sent";
    case "folder":
      return null;
  }
}

/** The part of an address before the @, which is what fits beside a row name. */
export function shortAddress(address: string): string {
  const at = address.indexOf("@");
  return at > 0 ? address.slice(0, at + 1) : address;
}

/**
 * A view's name and icon as Favorites draws it: the row's own name, and which
 * mailbox it is in. The combined pair names itself ("All inboxes").
 */
export function viewLabel(
  t: T,
  view: MailView,
  mailboxes: MailboxView[],
): { label: string; sub: string | null; Icon: LucideIcon } {
  if (view.kind === "sentAll") return { label: t("mail.fav.allSent"), sub: null, Icon: Send };
  if (view.kind === "threads" && view.mailbox === ALL) return { label: t("mail.fav.allInbox"), sub: null, Icon: Inbox };
  const box = mailboxes.find((m) => m.id === view.mailbox);
  const sub = box ? shortAddress(box.address) : null;
  if (view.kind === "folder") return { label: view.path.split(/[/.]/).pop() || view.path, sub, Icon: Folder };
  const row = rowOf(view)!;
  return { label: rowLabel(t, row), sub, Icon: ROW_ICON[row] };
}

/** The count a row carries: unread in an inbox, the letters waiting in Drafts and Scheduled. */
export function viewCount(view: MailView, mailboxes: MailboxView[]): number {
  const pick = (box: MailboxView) => {
    if (view.kind === "threads" && view.status === "open") return box.unread;
    if (view.kind === "drafts") return view.scheduled ? box.scheduled : box.drafts;
    return 0;
  };
  if (view.kind === "sentAll" || view.kind === "folder" || view.kind === "sent") return 0;
  if (view.mailbox === ALL) return mailboxes.reduce((n, m) => n + pick(m), 0);
  const box = mailboxes.find((m) => m.id === view.mailbox);
  return box ? pick(box) : 0;
}
